import React, { useState, useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import DataTable from '../../components/reusables/DataTable';
import { Badge } from '@appkit4/react-components/badge';
import Modal from '../../components/reusables/Modal';
import { conflictResolveContext } from '../../types/conflictResolveContext';

type TargetPool = {
    pool: string;
    percentage: string;
};

type ConflictingRule = {
    ruleId: string;
    ruleShortName: string;
    targetPools: TargetPool[];
};

type ConflictPerRuleRow = {
    conflictingRules: [ConflictingRule, ConflictingRule];
    numberOfConflictingAssets: number;
};

interface ResolvePerRuleModalProps {
    visible: boolean;
    onClose: () => void;
    context: conflictResolveContext;
    onResolve?: (
        context: conflictResolveContext,
        chosenRule: { ruleId: string; ruleShortName: string },
        rejectedRule: { ruleId: string; ruleShortName: string },
        numberOfAssets: number
    ) => void;
}

const makeRowKey = (row: ConflictPerRuleRow): string =>
    `${row.conflictingRules[0].ruleId}::${row.conflictingRules[1].ruleId}`;

// ---- Dummy data per source ----
const fixedAssetRuleConflicts: ConflictPerRuleRow[] = [
    {
        conflictingRules: [
            {
                ruleId: 'R001',
                ruleShortName: 'High Split Rule',
                targetPools: [{ pool: 'MP', percentage: '100%' }],
            },
            {
                ruleId: 'R002',
                ruleShortName: 'Impact Split Rule',
                targetPools: [
                    { pool: 'FY', percentage: '80%' },
                    { pool: 'SRP', percentage: '20%' },
                ],
            },
        ],
        numberOfConflictingAssets: 56,
    },
    {
        conflictingRules: [
            {
                ruleId: 'R001',
                ruleShortName: 'High Split Rule',
                targetPools: [{ pool: 'MP', percentage: '100%' }],
            },
            {
                ruleId: 'R003',
                ruleShortName: 'Impact Split Rule',
                targetPools: [{ pool: 'FY', percentage: '100%' }],
            },
        ],
        numberOfConflictingAssets: 45,
    },
];

const projectDataRuleConflicts: ConflictPerRuleRow[] = [
    {
        conflictingRules: [
            {
                ruleId: 'R010',
                ruleShortName: 'Electric Split Rule',
                targetPools: [{ pool: 'NQ', percentage: '100%' }],
            },
            {
                ruleId: 'R011',
                ruleShortName: 'Keyword Rule',
                targetPools: [{ pool: 'KW', percentage: '100%' }],
            },
        ],
        numberOfConflictingAssets: 120,
    },
    {
        conflictingRules: [
            {
                ruleId: 'R010',
                ruleShortName: 'Electric Split Rule',
                targetPools: [{ pool: 'NQ', percentage: '100%' }],
            },
            {
                ruleId: 'R012',
                ruleShortName: 'Planning Rule',
                targetPools: [{ pool: 'PL', percentage: '80%' }],
            },
        ],
        numberOfConflictingAssets: 85,
    },
];

const getRuleConflictsData = (
    context: conflictResolveContext
): ConflictPerRuleRow[] => {
    if (context.source === 'fixedAsset') {
        return fixedAssetRuleConflicts;
    }
    return projectDataRuleConflicts;
};

// ---- Component ----
const ResolvePerRuleModal: React.FC<ResolvePerRuleModalProps> = ({
    visible,
    onClose,
    context,
    onResolve,
}) => {
    const [selections, setSelections] = useState<Record<string, string>>({});

    const conflictsPerRuleData = getRuleConflictsData(context);

    const handleRuleClick = (
        row: ConflictPerRuleRow,
        chosenRule: ConflictingRule,
        ruleIndex: number
    ) => {
        const rowKey = makeRowKey(row);
        setSelections((prev) => ({ ...prev, [rowKey]: chosenRule.ruleId }));

        if (onResolve) {
            const rejectedRule = row.conflictingRules[ruleIndex === 0 ? 1 : 0];
            onResolve(
                context,
                {
                    ruleId: chosenRule.ruleId,
                    ruleShortName: chosenRule.ruleShortName,
                },
                {
                    ruleId: rejectedRule.ruleId,
                    ruleShortName: rejectedRule.ruleShortName,
                },
                row.numberOfConflictingAssets
            );
        }
    };

    const isSelected = (
        row: ConflictPerRuleRow,
        rule: ConflictingRule
    ): boolean => {
        const rowKey = makeRowKey(row);
        return selections[rowKey] === rule.ruleId;
    };

    const modalTitle =
        context.source === 'projectData'
            ? `Resolve Conflicts — Project ${(context as any).projectId ?? ''}`
            : 'Resolve Conflicts';

    const ruleConflictColumns: ColumnDef<ConflictPerRuleRow, any>[] = useMemo(
        () => [
            {
                id: 'conflictingRules',
                header: 'Conflicting Rules',
                cell: ({ row }) => (
                    <div className="conflicting-rules-cell">
                        {row.original.conflictingRules.map(
                            (rule, ruleIndex) => (
                                <button
                                    key={rule.ruleId}
                                    type="button"
                                    className={`rule-panel ${isSelected(row.original, rule) ? 'rule-panel--selected' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRuleClick(
                                            row.original,
                                            rule,
                                            ruleIndex
                                        );
                                    }}
                                >
                                    <span className="rule-panel__name">
                                        {rule.ruleShortName}
                                    </span>
                                    <div className="rule-panel__pools">
                                        {rule.targetPools.map((pool, i) => (
                                            <Badge
                                                key={i}
                                                value={`${pool.pool} (${pool.percentage})`}
                                            />
                                        ))}
                                    </div>
                                </button>
                            )
                        )}
                    </div>
                ),
            },
            {
                accessorKey: 'numberOfConflictingAssets',
                header: 'Number of Conflicting Assets',
            },
        ],
        [selections]
    );

    return (
        <Modal
            visible={visible}
            title={modalTitle}
            onClose={onClose}
            modalStyle={{ width: '900px', maxWidth: '95vw' }}
        >
            <div className="resolve-per-rule-modal">
                {context.source === 'projectData' && context.projectId && (
                    <div className="resolve-per-rule-modal__context-info">
                        <span>Project: {context.projectId}</span>
                        {context.version && (
                            <span> &middot; Version: {context.version}</span>
                        )}
                    </div>
                )}

                <div className="resolve-per-rule-modal__table-wrapper">
                    <DataTable
                        data={conflictsPerRuleData}
                        columns={ruleConflictColumns}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default ResolvePerRuleModal;
